import React, { Ref } from 'react';
import classnames from 'classnames';
import { parseMetadata, noop } from '@highlight-ui/utils-commons';
import { Icon } from '@highlight-ui/icon';
import { Checkbox } from '@highlight-ui/checkbox';

import styles from './Option.module.scss';
import { SelectOption, SelectOptionType } from '../../types';

import {
  SingleLineOptionLabel,
  MultiLineOptionLabel,
  ColoredOptionLabel,
  AvatarOptionLabel,
} from './OptionVariation';

const optionLabelComponents = {
  'single-line': SingleLineOptionLabel,
  'multi-line': MultiLineOptionLabel,
  colored: ColoredOptionLabel,
  avatar: AvatarOptionLabel,
};

export type OptionProps<T extends SelectOption> = {
  disabled?: boolean;
  highlightedText: string;
  onSelect: (data: T) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLLIElement>) => void;
  option: T;
  optionStyle: 'checkmark' | 'checkbox';
  optionType: SelectOptionType;
  selected: boolean;
  className?: string;
  optionRef?: Ref<HTMLLIElement>;
  enableSubLabelSearch?: boolean;
};

const Option = <T extends SelectOption>({
  disabled,
  highlightedText,
  onSelect,
  onKeyDown = noop,
  option,
  optionStyle,
  optionType,
  selected,
  className,
  optionRef,
  enableSubLabelSearch,
}: OptionProps<T>) => (
  <li
    ref={optionRef}
    aria-selected={selected}
    className={classnames(
      styles.option,
      {
        [styles.selectedText]: selected && optionStyle === 'checkmark',
        [styles.multiLineOption]: optionType === 'multi-line',
        [styles.disabled]: disabled,
      },
      className,
    )}
    onClick={!disabled ? () => onSelect(option) : noop}
    onKeyDown={!disabled ? onKeyDown : noop}
    role="option"
    title={option.label}
    tabIndex={-1}
    aria-disabled={disabled}
    {...parseMetadata(option.metadata)}
  >
    {optionStyle === 'checkbox' ? (
      <Checkbox
        className={classnames(styles.optionCheckbox, {
          [styles.multiLineCheckbox]: optionType === 'multi-line',
        })}
        checked={selected}
        readOnly
        disabled={disabled}
        tabIndex={-1}
      />
    ) : (
      <Icon
        className={classnames(styles.optionIcon, {
          [styles.multiLineIcon]: optionType === 'multi-line',
        })}
        name="check"
        size={14}
        type="solid"
      />
    )}

    <div className={styles.labelWrapper}>
      {React.createElement(optionLabelComponents[optionType], {
        option,
        highlightedText,
        selected: optionStyle !== 'checkbox' ? selected : false,
        disabled,
        enableSubLabelSearch,
      })}
    </div>
  </li>
);

Option.displayName = 'Option';

export default Option;
